/* eslint-disable prettier/prettier  */
// React
import React from "react";
import PropTypes from "prop-types";

// Components
import {
  SEGMENT as TEXT_SEGMENT,
  getNormalizedSegment,
} from "@gerenciatd/next13.enums.segments";
import getColor, {
  COLOR_DICTIONARY as TEXT_COLOR,
  OPACITY as TEXT_OPACITY,
} from "@gerenciatd/next13.enums.colors";

// Utils
import { extendTextInfoClassNames, getProperKind } from "./utils";

// Enums
import {
  TEXT_ALIGN,
  TEXT_BORDER,
  TEXT_DECORATION,
  TEXT_DISPLAY,
  TEXT_WRAP,
  TEXT_FONT_WEIGHT,
  TEXT_HEADING_LEVEL,
  TEXT_KIND,
  TEXT_SIZE,
} from "./enums";

export {
  TEXT_ALIGN,
  TEXT_BORDER,
  TEXT_COLOR,
  TEXT_OPACITY,
  TEXT_DECORATION,
  TEXT_DISPLAY,
  TEXT_WRAP,
  TEXT_FONT_WEIGHT,
  TEXT_HEADING_LEVEL,
  TEXT_KIND,
  TEXT_SEGMENT,
  TEXT_SIZE,
};

const Text = ({ component, ...restProps }) => {
  const Component = component;
  return <Component {...restProps}/>;
};

/**
 * @name TextInfo
 * @description atomic component
 * @see {@link https://bit.cloud/gerenciatd/next13/components/text-info}
 * @param {any} props
 * @returns {React.FC}
 */

const COMPONENT_NAME = "TextInfo";

const TextInfo = (props) => {
  const {
    className = "",
    segment: segmentProp,
    kind,
    text,
    color,
    opacity,
    bgOpacity,
    backgroundColor,
    level,
    custoObjectInfo,
    cyData,
    decoration,
    sizeLegacy,
    ...restProps
  } = props;

  const normalizedKind = getProperKind(kind);

  if (!normalizedKind) {
    return null;
  }

  const segment = getNormalizedSegment(segmentProp);
  const colorOpacity = getColor(color, opacity, segment);
  const bgColorOpacity =
    backgroundColor && backgroundColor !== "transparent"
      ? getColor(backgroundColor, bgOpacity, segment)
      : "transparent";
  const extendedClassNames = extendTextInfoClassNames({
    segment,
    kind: normalizedKind,
    decoration,
    sizeLegacy,
    ...restProps,
  });

  const componentClassName = `text${className ? ` ${className}` : ""} text-color--${segment}--${color.toLowerCase()}${extendedClassNames}${
    custoObjectInfo ? " custo-editable" : ""
  }`;

  return (
    <Text
      component={
        normalizedKind === TEXT_KIND.HEADING
          ? `${normalizedKind}${level}`
          : normalizedKind
      }
      className={componentClassName}
      dangerouslySetInnerHTML={{ __html: text }}
      onClick={custoObjectInfo ? () => custoObjectInfo.handler(custoObjectInfo.entryPoint) : null}
      data-cy={cyData}
      style={{ color: colorOpacity, backgroundColor: bgColorOpacity }}
    />
  );
};

TextInfo.propTypes = {
  /**
   * Texto que se mostrará en el componente.
   */
  text: PropTypes.string.isRequired,
  /**
   * TEXT_KIND -> Decidirá si el componente es un heading, paragraph o span.
   */
  kind: PropTypes.string,
  /**
   * TEXT_HEADING_LEVEL -> Si el componente es un heading, se especifica el nivel de este (h1, h2, h3, etc).
   */
  level: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /**
   * Clase para añadirle estilos extras al icono.
   */
  className: PropTypes.string,
  /**
   * TEXT_COLOR -> Color del texto.
   */
  color: PropTypes.string,
  /**
   * TEXT_SIZE -> Tamaño del texto. Se elegirá entre Title, Display o Caption (cada uno con sus niveles correspondientes).
   */
  size: PropTypes.string,
  /**
   * TEXT_FONT_WEIGHT -> Grosor del texto (Regular, Light o Bold).
   */
  fontType: PropTypes.string,
  /**
   * TEXT_ALIGN -> Alineación del texto (centrado, a la izquierda/derecha o justificado).
   */
  align: PropTypes.string,
  /**
   * TEXT_DISPLAY -> Especifica si el texto es tratado como un elemento block o inline.
   */
  display: PropTypes.string,
  /**
  * TEXT_WRAP -> Especifica si el texto se corta para no dejar solo una palabra en la última línea (pretty),
  * o se balanceara en dos líneas del mismo tamaño(balance),
  * además permite wrap y nowrap.
 */
  wrap: PropTypes.string,
  /**
   * TEXT_DECORATION -> Si el texto tendrá subrayado o una línea cruzada.
   */
  decoration: PropTypes.string,
  /**
   * TEXT_BORDER -> Redondeado del texto.
   */
  border: PropTypes.string,
  /**
   * TEXT_COLOR -> Color del fondo del texto.
   */
  backgroundColor: PropTypes.string,
  /**
   * TEXT_OPACITY -> Opacidad del texto.
   */
  opacity: PropTypes.string,
  /**
   * TEXT_OPACITY -> Opacidad del fondo.
   */
  bgOpacity: PropTypes.string,
  /**
   * Objeto para la customización del icono en dev.
   */
  custoObjectInfo: PropTypes.shape({
    componentId: PropTypes.string,
    componentInstance: PropTypes.string,
    editFunction: PropTypes.func,
  }),
  cyData: PropTypes.string,
  sizeLegacy: PropTypes.bool,
};

TextInfo.defaultProps = {
  kind: TEXT_KIND.SPAN,
  level: TEXT_HEADING_LEVEL.H1,
  className: "",
  color: TEXT_COLOR.BLACK,
  size: TEXT_SIZE.CAPTION2,
  fontType: TEXT_FONT_WEIGHT.REGULAR,
  custoObjectInfo: null,
  align: TEXT_ALIGN.LEFT,
  border: TEXT_BORDER.NONE,
  decoration: TEXT_DECORATION.NONE,
  display: TEXT_DISPLAY.BLOCK,
  wrap: TEXT_WRAP.PRETTY,
  backgroundColor: TEXT_COLOR.TRANSPARENT,
  opacity: TEXT_OPACITY[100],
  bgOpacity: TEXT_OPACITY[100],
  sizeLegacy: false,
  cyData: "",
};

export default TextInfo;
